import { useState } from "react"
import { mutate } from "swr"
import { i18n } from "@dehub/theme"
import {
  SizeScaleApi,
  ClassificationRuleApi,
  ConsolidationRuleApi,
  GovernanceConfigApi,
  ExternalPartyFullApi,
  HolidayCalendarApi,
  NotificationTemplateApi,
} from "../../../apis"
import { message } from "../../../utils/message"
import type { ICreateSizeScaleInput, ICreateSizeDefinitionInput } from "../../../apis/size-scale"
import type { ICreateClassificationRuleInput } from "../../../apis/classification-rule"
import type { ICreateConsolidationRuleInput } from "../../../apis/consolidation-rule"
import type { IGovernanceConfig } from "../../../apis/governance-config"
import type { ICreateExternalPartyInput } from "../../../apis/external-party-full"
import type { ICreateHolidayInput } from "../../../apis/holiday-calendar"
import type { ICreateNotificationTemplateInput } from "../../../apis/notification-template"

const invalidate = (prefix: string): Promise<void> =>
  mutate(
    (key) => Array.isArray(key) && key[0] === prefix,
    undefined,
    { revalidate: true },
  ).then(() => undefined)

export function useSettingsMutations() {
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  async function wrap<T>(
    fn: () => Promise<T>,
    onSuccess?: () => void | Promise<void>,
    successKey: "savedSuccess" | "deleteSuccess" | null = "savedSuccess",
  ): Promise<T> {
    setSubmitting(true)
    setError(null)
    try {
      const r = await fn()
      await onSuccess?.()
      if (successKey) {
        message.success(i18n.t(`common:messages.${successKey}`))
      }
      return r
    } catch (e) {
      const err = e instanceof Error ? e : new Error(String(e))
      setError(err)
      message.error(err.message || i18n.t("common:messages.error"))
      throw err
    } finally {
      setSubmitting(false)
    }
  }

  return {
    submitting,
    error,
    sizeScale: {
      create: (d: ICreateSizeScaleInput) =>
        wrap(() => SizeScaleApi.create(d), () => invalidate("size-scales")),
      update: (id: string, p: Partial<ICreateSizeScaleInput>) =>
        wrap(() => SizeScaleApi.update(id, p), () => invalidate("size-scales")),
      remove: (id: string) =>
        wrap(() => SizeScaleApi.remove(id), () => invalidate("size-scales"), "deleteSuccess"),
    },
    // §9.4 — nested SizeDefinition CRUD on a scale. Definition mutations
    // also bust the size-scales list so the inline definition badge counts
    // refresh.
    sizeDefinition: {
      create: (sizeScaleId: string, d: ICreateSizeDefinitionInput) =>
        wrap(
          () => SizeScaleApi.definitions.create(sizeScaleId, d),
          async () => {
            await invalidate(`size-definitions:${sizeScaleId}`)
            await invalidate("size-scales")
          },
        ),
      update: (sizeScaleId: string, id: string, p: Partial<ICreateSizeDefinitionInput>) =>
        wrap(
          () => SizeScaleApi.definitions.update(sizeScaleId, id, p),
          async () => {
            await invalidate(`size-definitions:${sizeScaleId}`)
            await invalidate("size-scales")
          },
        ),
      remove: (sizeScaleId: string, id: string) =>
        wrap(
          () => SizeScaleApi.definitions.remove(sizeScaleId, id),
          async () => {
            await invalidate(`size-definitions:${sizeScaleId}`)
            await invalidate("size-scales")
          },
          "deleteSuccess",
        ),
    },
    classificationRule: {
      create: (d: ICreateClassificationRuleInput) =>
        wrap(() => ClassificationRuleApi.create(d), () => invalidate("classification-rules")),
      update: (id: string, p: Partial<ICreateClassificationRuleInput>) =>
        wrap(() => ClassificationRuleApi.update(id, p), () => invalidate("classification-rules")),
      remove: (id: string) =>
        wrap(() => ClassificationRuleApi.remove(id), () => invalidate("classification-rules"), "deleteSuccess"),
    },
    consolidationRule: {
      create: (d: ICreateConsolidationRuleInput) =>
        wrap(() => ConsolidationRuleApi.create(d), () => mutate("settings.consolidation-rule.list")),
      update: (id: string, p: Partial<ICreateConsolidationRuleInput>) =>
        wrap(() => ConsolidationRuleApi.update(id, p), () => mutate("settings.consolidation-rule.list")),
      remove: (id: string) =>
        wrap(() => ConsolidationRuleApi.remove(id), () => mutate("settings.consolidation-rule.list"), "deleteSuccess"),
    },
    governance: {
      update: (p: Partial<IGovernanceConfig>) =>
        wrap(() => GovernanceConfigApi.update(p), () => invalidate("governance-config")),
    },
    externalParty: {
      create: (d: ICreateExternalPartyInput) =>
        wrap(() => ExternalPartyFullApi.create(d), () => invalidate("external-parties")),
      update: (id: string, p: Partial<ICreateExternalPartyInput>) =>
        wrap(() => ExternalPartyFullApi.update(id, p), () => invalidate("external-parties")),
      remove: (id: string) =>
        wrap(() => ExternalPartyFullApi.remove(id), () => invalidate("external-parties"), "deleteSuccess"),
    },
    holiday: {
      create: (d: ICreateHolidayInput) =>
        wrap(() => HolidayCalendarApi.create(d), () => mutate("settings.holiday-calendar.list")),
      update: (id: string, p: Partial<ICreateHolidayInput>) =>
        wrap(() => HolidayCalendarApi.update(id, p), () => mutate("settings.holiday-calendar.list")),
      remove: (id: string) =>
        wrap(() => HolidayCalendarApi.remove(id), () => mutate("settings.holiday-calendar.list"), "deleteSuccess"),
    },
    notifyTemplate: {
      create: (d: ICreateNotificationTemplateInput) =>
        wrap(() => NotificationTemplateApi.create(d), () => mutate("settings.notification-template.list")),
      update: (id: string, p: Partial<ICreateNotificationTemplateInput>) =>
        wrap(() => NotificationTemplateApi.update(id, p), () => mutate("settings.notification-template.list")),
      remove: (id: string) =>
        wrap(() => NotificationTemplateApi.remove(id), () => mutate("settings.notification-template.list"), "deleteSuccess"),
    },
  }
}
